import { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";

function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axiosInstance.get("/api/settings");
      setSettings(res.data);
    } catch (err) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axiosInstance.put("/api/settings", {
        trust_name: settings.trust_name,
        school_name: settings.school_name,
        tagline: settings.tagline,
        established_year: settings.established_year || null,
        theme_primary_color: settings.theme_primary_color,
        theme_secondary_color: settings.theme_secondary_color,
        default_language: settings.default_language,
      });
      setSettings(res.data);
      toast.success("Settings updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploadingLogo(true);
    try {
      const res = await axiosInstance.post("/api/settings/logo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSettings(res.data);
      toast.success("Logo updated");
    } catch (err) {
      toast.error(err.response?.data?.error || "Logo upload failed");
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  const logoUrl = settings?.logo_path
    ? `${process.env.REACT_APP_API_URL}/${settings.logo_path}`
    : null;

  return (
    <div>
      <h4 className="mb-4">Site Settings</h4>

      <div className="row g-4">
        {/* Logo card */}
        <div className="col-12 col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body text-center">
              <h6 className="card-title mb-3">School Logo</h6>

              <div
                className="d-flex align-items-center justify-content-center bg-light rounded mb-3 mx-auto"
                style={{ width: "160px", height: "160px", overflow: "hidden" }}
              >
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="School logo"
                    className="img-fluid"
                    style={{ maxHeight: "100%" }}
                  />
                ) : (
                  <i
                    className="bi bi-image text-muted"
                    style={{ fontSize: "2.5rem" }}
                  ></i>
                )}
              </div>

              <label className="btn btn-outline-primary btn-sm">
                {uploadingLogo ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="bi bi-upload me-1"></i> Change Logo
                  </>
                )}
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  hidden
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo}
                />
              </label>
              <p
                className="text-muted mt-2 mb-0"
                style={{ fontSize: "0.75rem" }}
              >
                PNG, JPG or WEBP
              </p>
            </div>
          </div>
        </div>

        {/* Settings form */}
        <div className="col-12 col-md-8">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <form onSubmit={handleSave}>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Trust Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="trust_name"
                      value={settings.trust_name || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">School Name</label>
                    <input
                      type="text"
                      className="form-control"
                      name="school_name"
                      value={settings.school_name || ""}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Tagline</label>
                    <input
                      type="text"
                      className="form-control"
                      name="tagline"
                      value={settings.tagline || ""}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-6">
                    <label className="form-label">Established Year</label>
                    <input
                      type="number"
                      className="form-control"
                      name="established_year"
                      value={settings.established_year || ""}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-6">
                    <label className="form-label">Language</label>
                    <select
                      className="form-select"
                      name="default_language"
                      value={settings.default_language || "en"}
                      onChange={handleChange}
                    >
                      <option value="en">English</option>
                      <option value="kn">Kannada</option>
                    </select>
                  </div>

                  <div className="col-6">
                    <label className="form-label">Primary Color</label>
                    <input
                      type="color"
                      className="form-control form-control-color w-100"
                      name="theme_primary_color"
                      value={settings.theme_primary_color || "#0d6efd"}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-6">
                    <label className="form-label">Secondary Color</label>
                    <input
                      type="color"
                      className="form-control form-control-color w-100"
                      name="theme_secondary_color"
                      value={settings.theme_secondary_color || "#6c757d"}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary mt-4"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
